import React, { useContext, useState, useCallback } from 'react';
import { cartDailySVC } from '@com/core/common/services/api';
import { AppContext } from '@com/core/common/contexts/appContext';
import { CartContext } from '@com/core/common/contexts/cartContextDaily';
import { useTranslation } from '@com/core/common/translations/i18n';
import useOnclickOutside from 'react-cool-onclickoutside';
import styled from 'styled-components';
import { TDeliverySlot, TDeliveryTimeSlot } from '@com/core/common/types/cartDaily';
import { CDN_IMG_BASE } from '@com/core/common/baseConfig';
import BlockButton from './BlockButton';
import Loader from './Loader';

type TProps = {
    date: string;
    day: string;
    time: string;
    isChangeAllowed?: boolean;
    groupIx?: number;
    size?: 'small' | 'regular';
};

const DeliverySlot = ({
    date,
    day,
    groupIx = 0,
    isChangeAllowed = false,
    size = 'regular',
    time,
}: TProps): JSX.Element | null => {
    const { t } = useTranslation('common');
    const { changeDeliverySlot } = useContext(CartContext);
    const { locale } = useContext(AppContext);

    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedTime, setSelectedTime] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [slotsData, setSlotsData] = useState<TDeliverySlot[] | null>(null);

    const ref = useOnclickOutside(() => {
        setShowMenu(false);
        resetData();
    });

    const getData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await cartDailySVC(locale.apiLocale).getDeliverySlots(groupIx);
            if (data.error) {
                setError(data.error);
            } else {
                setSlotsData(data.deliverySlots);
                setSelectedDate(data.deliverySlots.find(slot => slot.isDefault)?.deliveryDate || '');
            }
            setIsLoading(false);
        } catch (err) {
            setError(err.error || 'Something went wrong!');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupIx]);

    const getSlots = () => {
        if (!showMenu) {
            setShowMenu(true);
            if (!slotsData) getData();
        } else {
            setShowMenu(false);
            resetData();
        }
    };

    const resetData = () => {
        setSelectedDate((slotsData && slotsData.find(slot => slot.isDefault)?.deliveryDate) || '');
        setSelectedTime('');
    };

    const applyChange = () => {
        setShowMenu(false);
        changeDeliverySlot(groupIx, { deliveryTimeSlotCode: selectedTime, deliveryDate: selectedDate });
    };

    const getDateNumber = (fullDate: string) => new Date(fullDate).getDate();
    const getTimeSlots = slotsData && selectedDate && slotsData.find(slot => slot.deliveryDate === selectedDate);

    return (
        <Container ref={ref} className={size}>
            <Trigger disabled={!isChangeAllowed} type="button" onClick={getSlots}>
                <span className="dateContainer">
                    {day} {getDateNumber(date)}
                </span>
                <span className="timeContainer">
                    {time}
                    {isChangeAllowed && <img src={`${CDN_IMG_BASE}/simpleIcons/chevron-down.svg`} alt="Change" />}
                </span>
            </Trigger>

            {showMenu && (
                <Menu>
                    {// eslint-disable-next-line no-nested-ternary
                    isLoading ? (
                        <LoaderContainer>
                            <Loader color="brand" dotSize={10} />
                        </LoaderContainer>
                    ) : slotsData ? (
                        <div>
                            <p className="heading">{t('pick-a-date')}</p>
                            <div className="dateOptions">
                                {slotsData.map(slot => (
                                    <div className="buttonContainer" key={slot.deliveryDate}>
                                        <button
                                            type="button"
                                            className={slot.deliveryDate === selectedDate ? 'selected' : ''}
                                            disabled={slot.deliveryDate === selectedDate}
                                            onClick={() => {
                                                setSelectedDate(slot.deliveryDate);
                                                setSelectedTime('');
                                            }}>
                                            <span className="date">{getDateNumber(slot.deliveryDate)}</span>
                                            <span className="dateLabel">{slot.deliveryDateParam}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {getTimeSlots && (
                                <>
                                    <p className="heading">{t('pick-a-time')}</p>
                                    <div className="timeOptions">
                                        {getTimeSlots.deliveryTimeSlots.map((timeSlot: TDeliveryTimeSlot) => {
                                            const isSelected =
                                                (!selectedTime && timeSlot.isDefault) || timeSlot.code === selectedTime;
                                            return (
                                                <div className="buttonContainer" key={timeSlot.code}>
                                                    <button
                                                        type="button"
                                                        className={`${isSelected ? 'selected' : ''}${
                                                            !timeSlot.isAvailable ? 'unavailable' : ''
                                                        }`}
                                                        disabled={!timeSlot.isAvailable || isSelected}
                                                        onClick={() => setSelectedTime(timeSlot.code)}>
                                                        {timeSlot.name}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            <BlockButton variant="secondary" onClick={applyChange} disabled={!selectedTime}>
                                {t('change')}
                            </BlockButton>
                        </div>
                    ) : (
                        <p className="error">{t('_delivery-slots-fetch-error')}</p>
                    )}
                </Menu>
            )}
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;

const Trigger = styled.button`
    background-color: ${({ theme }): string => theme.colors.bgSecondary};
    font-size: 0.75rem;
    font-weight: bold;
    display: inline-flex;
    align-items: center;
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    transition: opacity ease-in-out 0.2s;

    &[disabled] {
        pointer-events: none;
    }

    &:hover {
        opacity: 0.7;
    }

    .dateContainer {
        height: 34px;
        padding: 0 12px;
        display: flex;
        align-items: center;
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        border: 1px solid ${({ theme }): string => theme.colors.border};
        color: ${({ theme }): string => theme.colors.brandSecondary};
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};

        .small & {
            height: 28px;
        }
    }

    .timeContainer {
        display: flex;
        align-items: center;
        padding: 0 12px;
    }

    img {
        width: 14px;
        ${({ theme: { left } }): string => `margin-${left}: 6px`};
        ${({ theme: { right } }): string => `margin-${right}: -4px`};
    }
`;

const LoaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
`;

const Menu = styled.div`
    z-index: 50;
    position: absolute;
    top: auto;
    width: 155%;
    right: 0;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    margin-top: 8px;
    box-shadow: 0px 37px 104px rgba(0, 0, 0, 0.15);
    padding: 13px 16px 16px;
    min-height: 200px;

    .heading {
        font-weight: 600;
        font-size: 0.875rem;
        margin-bottom: 10px;
    }

    .dateOptions {
        display: flex;
        flex-wrap: wrap;
        margin: -10px 0 16px -10px;

        .buttonContainer {
            flex: 0 0 20%;
            padding: 10px 0 0 10px;
        }

        button {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .date {
            width: 32px;
            height: 32px;
            border-radius: 100%;
            border: 1px solid ${({ theme }): string => theme.colors.border};
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: bold;
        }

        .dateLabel {
            color: ${({ theme }): string => theme.colors.bodyLight};
            font-size: 0.75rem;
            margin-top: 3px;
        }

        button:hover {
            .date {
                border-color: ${({ theme }): string => theme.colors.brandPrimary};
            }
        }

        button.selected {
            .date {
                border-color: ${({ theme }): string => theme.colors.brandPrimary};
                background-color: ${({ theme }): string => theme.colors.brandPrimary};
                color: ${({ theme }): string => theme.colors.bodyAlt};
            }

            .dateLabel {
                color: ${({ theme }): string => theme.colors.body};
            }
        }
    }

    .timeOptions {
        display: flex;
        flex-wrap: wrap;
        margin: -10px 0 18px -10px;

        .buttonContainer {
            flex: 0 0 50%;
            padding: 10px 0 0 10px;
        }

        button {
            width: 100%;
            height: 32px;
            border: 1px solid ${({ theme }): string => theme.colors.border};
            border-radius: 16px;
            font-size: 13px;
            font-weight: bold;
            padding: 0 12px;

            &:hover {
                border-color: ${({ theme }): string => theme.colors.brandPrimary};
            }

            &.selected {
                border-color: ${({ theme }): string => theme.colors.brandPrimary};
                background-color: ${({ theme }): string => theme.colors.brandPrimary};
                color: ${({ theme }): string => theme.colors.bodyAlt};
            }

            &.unavailable {
                border-color: ${({ theme }): string => theme.colors.controlDisabledBg};
                background-color: ${({ theme }): string => theme.colors.controlDisabledBg};
                color: ${({ theme }): string => theme.colors.controlDisabledBody};
            }
        }
    }

    p.error {
        font-size: 0.875rem;
    }
`;

export default DeliverySlot;
